// Client-added overlays (presentation-2 contract sections 5.1 and 5.5): the action bar, the moves
// drawer, choice and step popovers, the payment tray, card zoom and the turn banner.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Session, SessionState } from '../session/Session';
import type { TableModel } from '../model/table';
import { ownsDecision } from '../model/table';
import { allocationBounds, choiceEligible, currentStep, draftPrompt, initialAllocation, isComplete, startDraft, validateAllocation, type ChoiceView, type Draft, type StepValue } from '../model/choices';
import { choiceFamily, initialValues, matchFamily, type Family, type FamilyValues } from './family';
import type { ChoiceStep } from '../protocol/ui';
import { errorText } from '../protocol/wire';
import { usePlay, type PlayApi } from './PlayController';
import { StepControl, describeAnswer } from '../components/StepControls';
import { ChoicePanel } from '../components/ChoicePanel';
import { SelectionReview } from '../components/SelectionReview';
import { Slot } from '../components/ObjectCard';
import { usePresentation } from '../presentation/PresentationContext';
import { PieceFace } from '../bricks/Piece';
import { AUTO_ANSWER_TRADEOFF } from './autoAnswerSetting';
import { selectedObjectIds, tallyOf, tallyRuleFor, type Tally } from './tally';
import type { PresentationV2 } from '../presentation/types';
import { AltHoverCard } from './AltHoverCard';
import { useReplay } from '../scene/ViewContext';
import { PublicProgress } from '../components/PublicProgress';
import { CatalogMetadata } from '../components/CatalogMetadata';
import { fontStack } from '../theme/themeV2';
import { anchorRect, ANCHORS_CHANGED } from '../boards/runtime/semantic';
import { decisionIdentity, keyScrolls, mostlyVisible, revealElement, roomHeight, shouldReveal } from './revealLit';

type Allocate = Extract<ChoiceStep, { type: 'allocate' }>;

/** Viewport rectangle of an anchor element, kept current while it is shown. */
export function useAnchorRect(anchor: string | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!anchor) { setRect(null); return; }
    let frame = 0;
    const measure = () => {
      const el = document.querySelector(`[data-anchor="${CSS.escape(anchor)}"]`);
      setRect(anchorRect(anchor) ?? (el ? el.getBoundingClientRect() : null));
    };
    measure();
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener(ANCHORS_CHANGED, schedule);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', schedule); window.removeEventListener('scroll', schedule, true); window.removeEventListener(ANCHORS_CHANGED, schedule); };
  }, [anchor]);
  return rect;
}

/** Places a floating panel beside an anchor rectangle, inside the viewport. */
function Floating({ rect, children, label, className = '' }: { rect: DOMRect | null; children: React.ReactNode; label: string; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);
  const place = (next: { left: number; top: number }, floor: number) => {
    // Always on screen: a panel taller than the room above the floor starts at the top and scrolls inside.
    const top = Math.max(8, next.top);
    const maxHeight = Math.max(120, floor - top);
    setPos(p => (p && Math.abs(p.left - next.left) < 0.5 && Math.abs(p.top - top) < 0.5 && p.maxHeight === maxHeight ? p : { left: next.left, top, maxHeight }));
  };
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight, vw = window.innerWidth, vh = window.innerHeight, gap = 22;
    // Never cover the action bar (its Confirm finishes the step this panel belongs to).
    const barEl = document.querySelector('.action-bar');
    const bar = barEl?.getBoundingClientRect();
    // A header prompt sits above everything: panels stay below it instead of above it. A phone scene that puts the prompt in
    // its lower half (below the board) leaves more room above it, so the panel goes there instead of into the strip underneath.
    const header = !!barEl?.classList.contains('is-header');
    const barLow = header && !!bar && bar.height > 0 && bar.top > vh / 2;
    const floor = barLow ? bar!.top - 8 : header ? vh - 8 : bar && bar.height > 0 ? Math.min(vh - 8, bar.top - 8) : vh - 90;
    const ceiling = header && bar && !barLow ? bar.bottom + 8 : 8;
    if (!rect) { place({ left: (vw - w) / 2, top: Math.max(ceiling, Math.min((vh - h) / 2, floor - h)) }, floor); return; }
    let left = rect.right + gap, top = rect.top + rect.height / 2 - h / 2;
    if (left + w > vw - 8) left = rect.left - w - gap;
    if (left < 8) { left = Math.min(Math.max(8, rect.left + rect.width / 2 - w / 2), vw - w - 8); top = rect.bottom + gap; if (top + h > floor) top = rect.top - h - gap; }
    place({ left, top: Math.max(ceiling, Math.min(top, floor - h)) }, floor);
  }, [rect, children]);
  return (
    <div ref={ref} className={`floating ${className}`} role="dialog" aria-label={label}
      style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHeight } : { visibility: 'hidden', left: 0, top: 0 }} onClick={e => e.stopPropagation()}>
      {children}
    </div>
  );
}

// ---- Payment tray ------------------------------------------------------------------------

export function PaymentTray({ step, values, onChange }: { step: Allocate; values: number[]; onChange: (v: number[]) => void }) {
  const api = usePresentation();
  const total = values.reduce((a, b) => a + b, 0);
  const problem = validateAllocation(step, values);
  const target = step.sum ? step.sum.max : null;
  return (
    <div className="payment-tray">
      <p className="payment-title">{step.label}</p>
      <div className="payment-rows">
        {step.keys.map((k, i) => {
          const { min, max } = allocationBounds(k);
          const v = values[i] ?? 0;
          const icon = api.icon(k.key);
          return (
            <div key={k.key} className="payment-row">
              <span className="payment-key">{icon && <span className="payment-icon" aria-hidden="true"><Slot value={icon} /></span>}{k.label}</span>
              {k.available !== undefined && <span className="payment-held">have {k.available}</span>}
              <button type="button" className="stepper" aria-label={`Less ${k.label}`} onClick={() => onChange(values.map((x, j) => (j === i ? x - 1 : x)))} disabled={v <= min}>−</button>
              <output className="payment-value" aria-label={k.label}>{v}</output>
              <button type="button" className="stepper" aria-label={`More ${k.label}`} onClick={() => onChange(values.map((x, j) => (j === i ? x + 1 : x)))} disabled={v >= max}>+</button>
            </div>
          );
        })}
      </div>
      {target !== null && (
        <div className={`payment-progress${problem ? ' is-off' : ''}`} aria-hidden="true"><span style={{ width: `${Math.min(100, (total / Math.max(1, target)) * 100)}%` }} /></div>
      )}
      <p className={`payment-total${problem ? ' is-off' : ''}`}>{step.sum ? `${total} of ${step.sum.min === step.sum.max ? step.sum.min : `${step.sum.min}–${step.sum.max}`}` : `Total ${total}`}{problem ? ` · ${problem}` : ''}</p>
    </div>
  );
}

// ---- Popovers ----------------------------------------------------------------------------

/** One form for a choice family: every pick as buttons and every allocation as steppers, submitted at once. */
function FamilyForm({ family, button, seats, onSubmit }: { family: Family; button: string; seats: { id: string; label: string }[]; onSubmit: (choice: ChoiceView, draft: Draft) => void }) {
  const api = usePresentation();
  const [values, setValues] = useState<FamilyValues>(() => initialValues(family));
  const set = (field: string, value: FamilyValues[string]) => setValues(v => ({ ...v, [field]: value }));
  const empty = family.fields.find(f => f.type === 'allocate' ? !(values[f.field] as number[]).some(Boolean) : values[f.field] === undefined);
  const match = empty ? null : matchFamily(family, values);
  const status = empty ? `${empty.label}: ${empty.type === 'pick' ? 'choose one' : 'set an amount'}.` : match && 'problem' in match ? match.problem : null;
  // Packages often label seat options by seat ID; show the seat's name.
  const optionLabel = (label: string) => seats.find(s => s.id === label)?.label ?? label;
  return (
    <form className="family-form" onSubmit={e => { e.preventDefault(); if (match && !('problem' in match)) onSubmit(match.choice, match.draft); }}>
      {family.fields.map(f => f.type === 'pick' ? (
        <fieldset key={f.field} className="family-pick">
          <legend className="payment-title">{f.label}</legend>
          <div className="family-options">
            {f.options.map(o => (
              <button key={String(o.value)} type="button" className="popover-option" aria-pressed={values[f.field] === o.value} onClick={() => set(f.field, o.value)}>{optionLabel(o.label)}</button>
            ))}
          </div>
        </fieldset>
      ) : (
        <fieldset key={f.field} className="family-allocate payment-rows">
          <legend className="payment-title">{f.label}</legend>
          {f.keys.map((k, i) => {
            const all = values[f.field] as number[], v = all[i] ?? 0, { max } = allocationBounds(k);
            if (max <= 0 && v === 0) return null;
            const icon = api.icon(k.key);
            const change = (d: number) => set(f.field, all.map((x, j) => (j === i ? x + d : x)));
            return (
              <div key={k.key} className="payment-row">
                <span className="payment-key">{icon && <span className="payment-icon" aria-hidden="true"><Slot value={icon} /></span>}{k.label}</span>
                {k.available !== undefined && <span className="payment-held">have {k.available}</span>}
                <button type="button" className="stepper" aria-label={`Less ${k.label} (${f.label})`} onClick={() => change(-1)} disabled={v <= 0}>−</button>
                <output className="payment-value" aria-label={`${f.label}: ${k.label}`}>{v}</output>
                <button type="button" className="stepper" aria-label={`More ${k.label} (${f.label})`} onClick={() => change(1)} disabled={v >= max}>+</button>
              </div>
            );
          })}
        </fieldset>
      ))}
      <p className={`payment-total${status ? ' is-off' : ''}`} role="status">{status ?? 'Ready.'}</p>
      <button type="submit" className="bar-button bar-primary family-submit" disabled={!!status}>{button}</button>
    </form>
  );
}

/**
 * A popover option names what differs between the options on one target: the step's detail (a card, a rotation) when
 * that tells them apart, otherwise the choice itself (the same card given to different players).
 */
function optionText(entry: { label: string; detail?: string }, list: { detail?: string }[]): string {
  if (!entry.detail || list.length < 2) return entry.label;
  return list.filter(other => other.detail === entry.detail).length > 1 ? `${entry.label}: ${entry.detail}` : entry.detail;
}

function ChoicePopover({ play, choices, table }: { play: PlayApi; choices: ChoiceView[]; table: TableModel }) {
  const anchor = play.chooser!;
  const rect = useAnchorRect(anchor);
  const entries = play.targets.anchors.get(anchor) ?? [];
  // Entries of one action kind that vary only in bounds become one form; the rest stay a list.
  const families = useMemo(() => {
    const byKind = new Map<string, typeof entries>();
    for (const e of entries) {
      const c = choices.find(x => x.id === e.choiceId);
      if (c && e.answer === undefined && play.forms.has(c.action.kind)) byKind.set(c.action.kind, [...(byKind.get(c.action.kind) ?? []), e]);
    }
    return [...byKind.values()].flatMap(list => {
      const family = choiceFamily(list.map(e => choices.find(x => x.id === e.choiceId)!));
      return family ? [{ family, entries: list }] : [];
    });
  }, [entries, choices, play.forms]);
  const inFamily = new Set(families.flatMap(f => f.entries));
  const groups = new Map<string, typeof entries>();
  for (const e of entries) if (!inFamily.has(e)) groups.set(e.group, [...(groups.get(e.group) ?? []), e]);
  return (
    <Floating rect={rect} label={families[0]?.entries[0]?.group ?? 'Choose a move'} className={`choice-popover${families.length ? ' has-family' : ''}`}>
      {[...groups].map(([group, list]) => (
        <div key={group} className="popover-group">
          <p className="popover-heading">{group}</p>
          {list.map(e => (
            <button key={`${e.choiceId}-${String(e.answer)}`} data-choice={e.choiceId} type="button" className="popover-option" onClick={() => play.chooseEntry(anchor, e)}>{optionText(e, list)}</button>
          ))}
        </div>
      ))}
      {families.map(({ family, entries: list }) => (
        <div key={family.kind} className="popover-group">
          <p className="popover-heading">{list[0]!.group}</p>
          <FamilyForm family={family} seats={table.seats} button={list[0]!.button ?? 'Confirm'} onSubmit={(choice, draft) => play.submitDraft(anchor, choice, draft)} />
        </div>
      ))}
      <button type="button" className="popover-close" onClick={play.closeChooser}>Cancel</button>
    </Floating>
  );
}

function StepPopover({ play, allocation, setAllocation }: { play: PlayApi; allocation: number[] | null; setAllocation: (v: number[]) => void }) {
  const active = play.active!;
  const step = currentStep(active.draft)!;
  const rect = useAnchorRect(active.anchor);
  return (
    <Floating rect={rect} label={step.label} className={step.type === 'allocate' ? 'payment-popover' : 'step-popover'}>
      {step.type === 'allocate' && allocation
        ? <PaymentTray step={step} values={allocation} onChange={setAllocation} />
        : <StepControl key={active.draft.answers.length} step={step} group={active.choice.group} onAnswer={play.answerStep} onInspect={() => undefined} />}
    </Floating>
  );
}

function CardZoom({ play, table }: { play: PlayApi; table: TableModel }) {
  const { presentation } = usePresentation();
  const bodyFont = fontStack(presentation && 'format' in presentation ? presentation.theme.fonts?.body : undefined, 'system-ui, sans-serif');
  const object = play.inspected ? table.objects.get(play.inspected) : undefined;
  const rect = useAnchorRect(object ? `obj:${object.id}` : null);
  if (!object) return null;
  return (
    <Floating rect={rect} label={`Details: ${object.title}`} className="card-zoom">
      <div className="card-zoom-layout">
        <div className="card-zoom-face"><PieceFace object={object} zoneKind={null} width={320} /></div>
        <div className="card-zoom-prose" style={{ fontFamily: bodyFont }}>
          <p className="card-zoom-title">{object.title}</p>
          {object.label !== object.title && <p className="card-zoom-label">{object.label}</p>}
          {object.catalog?.text && <p className="card-zoom-text">{object.catalog.text}</p>}
          <CatalogMetadata object={object} />
          <PublicProgress object={object} table={table} />
          {object.details.length > 0 && <dl className="detail-list">{object.details.map(d => <div key={d.key}><dt>{d.key}</dt><dd>{d.value}</dd></div>)}</dl>}
          <button type="button" className="popover-close" onClick={() => play.inspect(null)}>Close</button>
        </div>
      </div>
    </Floating>
  );
}

// ---- Action bar ----------------------------------------------------------------------------

function draftSummary(play: PlayApi): string {
  const a = play.active!;
  const parts = a.draft.answers.filter(x => !x.forced).map(x => describeAnswer(x.step, x.value));
  return parts.join(' · ');
}

/** On wide scenes the bar floats over the top edge of the viewer's own mat, wherever the scene puts it. */
function useBarPlace(ref: React.RefObject<HTMLElement | null>, header: boolean): { bottom: number; left: number } | null {
  const [place, setPlace] = useState<{ bottom: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (header) { setPlace(null); return; }
    const bar = ref.current;
    const root = bar?.closest('.scene-root');
    if (!bar || !root || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const mat = root.classList.contains('is-narrow') ? null : root.querySelector('[data-self-mat]');
      if (!mat) { setPlace(null); return; }
      const r = mat.getBoundingClientRect(), box = root.getBoundingClientRect();
      const next = { bottom: Math.round(box.bottom - r.top - 10), left: Math.round(r.left + r.width / 2 - box.left) };
      setPlace(p => (p && p.bottom === next.bottom && p.left === next.left ? p : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [ref, header]);
  return place;
}

interface BarProps {
  play: PlayApi; table: TableModel; state: SessionState; choices: ChoiceView[]; allocation: number[] | null;
  session?: Session;
  /** Drawn inside the HUD (scene prompt "header") rather than floating over the mat. */
  header?: boolean;
  /** Move replay is showing a past position. */
  replaying?: boolean;
  /** Compact shell puts Undo beside the collapse control. */
  showUndo?: boolean;
}

/** Undo: enabled only when the host says this seat can undo now; Ctrl+Z and Ctrl+Shift+Z map to undo and redo. */
export function UndoButton({ session, state, off = false }: { session: Session; state: SessionState; off?: boolean }) {
  const undo = off || state.paused ? null : state.undo;
  const busy = !!state.pending;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.key.toLowerCase() !== 'z') return;
      if ((e.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
      const redo = e.shiftKey;
      if (redo ? undo?.canRedo : undo?.canUndo) { e.preventDefault(); session.control(redo ? 'redo' : 'undo'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, undo]);
  const title = state.paused ? 'This game is paused' : off ? 'Return to live play to undo' : !undo ? 'Undo needs a host update' : undo.canUndo ? `Undo: ${undo.label ?? 'your last move'} (Ctrl+Z)` : 'Nothing to undo';
  return (
    <>
      <button type="button" className="bar-button bar-quiet bar-undo" disabled={!undo?.canUndo || busy} title={title} aria-label={title}
        aria-keyshortcuts="Control+Z" onClick={() => session.control('undo')}>↶ Undo</button>
      {undo?.canRedo && <button type="button" className="bar-button bar-quiet bar-undo" disabled={busy} title="Redo (Ctrl+Shift+Z)" aria-keyshortcuts="Control+Shift+Z" onClick={() => session.control('redo')}>Redo ↷</button>}
    </>
  );
}

/** "3 needed · 2 selected" with a chip per resource type the selection supplies (interactions `tally`). */
function TallyReadout({ tally, selecting }: { tally: Tally; selecting: boolean }) {
  const api = usePresentation();
  const noun = tally.rule.noun ?? 'needed';
  return (
    <span className="bar-tally" role="status" aria-label={`${tally.needed ?? 'Some'} ${noun}${selecting ? `, ${tally.total} selected` : ''}`}>
      {tally.needed !== null && <span className="tally-needed"><b>{tally.needed}</b> {noun}</span>}
      {selecting && <span className="tally-selected"><b>{tally.total}</b> selected</span>}
      {selecting && tally.units.map(u => {
        const icon = api.icon(u.unit);
        return <span key={u.unit} className="tally-unit" title={`${u.count} ${u.unit}`}>{icon ? <span className="tally-icon" aria-hidden="true"><Slot value={icon} /></span> : <span className="tally-key">{u.unit}</span>}<b>{u.count}</b></span>;
      })}
    </span>
  );
}

export function ActionBar({ play, table, state, choices, allocation, session, header = false, replaying = false, showUndo = true }: BarProps) {
  const replay = useReplay();
  const presentationApi = usePresentation();
  const tallyRules = presentationApi.presentation && 'interactions' in presentationApi.presentation ? (presentationApi.presentation as PresentationV2).interactions.tally : undefined;
  // A phone's docked prompt can be folded to its first line; a new step opens it again.
  const [folded, setFolded] = useState(false);
  useEffect(() => { setFolded(false); }, [play.stepKey]);
  const barRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const element = barRef.current, root = element?.closest<HTMLElement>('.scene-root');
    if (!element || !root || header) return;
    const update = () => root.style.setProperty('--floating-prompt-height', `${element.getBoundingClientRect().height}px`);
    update(); const observer = new ResizeObserver(update); observer.observe(element);
    return () => { observer.disconnect(); root.style.removeProperty('--floating-prompt-height'); };
  }, [header]);
  // A phone's bottom sheets (a payment, a step) open above a header bar, so its Confirm and Cancel stay in reach.
  useEffect(() => {
    const element = barRef.current, root = element?.closest<HTMLElement>('.scene-root');
    if (!element || !root || !header) return;
    const update = () => root.style.setProperty('--bar-sheet-bottom', `${Math.max(0, root.getBoundingClientRect().bottom - element.getBoundingClientRect().top)}px`);
    update(); const observer = new ResizeObserver(update); observer.observe(element); observer.observe(root);
    return () => { observer.disconnect(); root.style.removeProperty('--bar-sheet-bottom'); };
  }, [header]);
  const barPlace = useBarPlace(barRef, header);
  // A header bar whose buttons run past its right edge (a phone with six roll buttons) says so: `can-scroll` fades that edge.
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [actionsScroll, setActionsScroll] = useState(false);
  useLayoutEffect(() => {
    const row = actionsRef.current;
    if (!row || !header || typeof ResizeObserver === 'undefined') { setActionsScroll(false); return; }
    const measure = () => setActionsScroll(row.scrollWidth - row.clientWidth - row.scrollLeft > 4);
    measure(); row.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure); observer.observe(row);
    return () => { row.removeEventListener('scroll', measure); observer.disconnect(); };
  });
  const [moreOpen, setMoreOpen] = useState(false);
  // A bar low on the screen (a phone's header bar sits above the dock) opens its overflow upward, where there is room. The
  // turn area clips and the bar's scroller masks anything that leaves them, so that menu is drawn on the scene root instead.
  const [moreUp, setMoreUp] = useState<{ root: HTMLElement; bottom: number } | null>(null);
  const [moveQuery, setMoveQuery] = useState('');
  const [movePage, setMovePage] = useState(0);
  const [playingTreasures, setPlayingTreasures] = useState(false);
  const sentTreasureRevision = useRef<number | null>(null);
  const treasureChoice = state.gameId === 'dominion-base-luau' ? choices.find(choice =>
    choice.group === 'Treasures' && choice.action.kind === 'play' && choice.steps.length === 0 && !choice.confirm && choiceEligible(table, choice)) : undefined;
  useEffect(() => { setPlayingTreasures(false); sentTreasureRevision.current = null; }, [state.gameId, state.matchId]);
  useEffect(() => {
    if (!playingTreasures) return;
    if (!session || play.active || replaying || state.connection !== 'open' || state.paused || table.finished || !ownsDecision(table)) {
      setPlayingTreasures(false); return;
    }
    // The motion frame briefly suspends input after each accepted play. Wait for
    // the displayed table to catch up instead of ending the sequence.
    if (!play.isMyTurn) return;
    if (state.pending) return;
    if (sentTreasureRevision.current === state.revision && state.notice?.tone === 'error') { setPlayingTreasures(false); return; }
    if (!treasureChoice) { setPlayingTreasures(false); return; }
    if (sentTreasureRevision.current === state.revision) return;
    sentTreasureRevision.current = state.revision;
    void session.submitDraft(startDraft(treasureChoice), 'Play all Treasures').then(sent => { if (!sent) setPlayingTreasures(false); });
  }, [playingTreasures, session, play.isMyTurn, play.active, replaying, state.connection, state.paused, state.pending, state.notice, state.revision, table.finished, treasureChoice]);
  useEffect(() => { if (play.active) setMoreOpen(false); }, [play.active]);
  const actor = table.seats.find(s => s.id === table.actor);
  const unresolved = play.targets.unresolved.length;
  const drawerButton = (
    <button type="button" className="bar-button bar-quiet bar-drawer" onClick={() => play.setDrawerOpen(true)} aria-keyshortcuts="M">
      All moves<span className="bar-hint"> (M)</span>{unresolved > 0 && <span className="bar-badge" aria-label={`${unresolved} without a table target`}>{unresolved}</span>}
    </button>
  );
  let lead: React.ReactNode, detail: React.ReactNode = null, actions: React.ReactNode = null;
  let idleWithoutPriority = false;
  const notice = play.lastError ?? state.queryCheck?.reason ?? (state.notice ? state.notice.text || errorText(state.notice.code) : null);
  if (replaying) {
    lead = 'Viewing a past move';
    detail = 'Older and Newer step through moves; Back to live returns.';
    actions = <button type="button" className="bar-button bar-primary" onClick={() => replay?.exit()}>Back to live</button>;
  } else if (table.finished) {
    lead = 'Game over';
    detail = table.result?.winners.length ? `${table.result.winners.map(w => table.seats.find(s => s.id === w)?.label ?? w).join(', ')} ${table.result.winners.length === 1 ? 'wins' : 'win'}.` : table.result?.reason ?? table.status;
  } else if (!table.selfSeat) {
    lead = 'You are watching'; detail = actor ? `${actor.label} is taking a turn.` : table.status;
  } else if (!play.isMyTurn && !play.active) {
    lead = <><span className="pulse" aria-hidden="true" />Waiting for {actor?.label ?? 'the next player'}</>; detail = table.status;
  } else if (state.pending) {
    lead = <><span className="pulse" aria-hidden="true" />Sending</>; detail = state.pending.label;
  } else if (play.active) {
    const a = play.active;
    const step = currentStep(a.draft);
    lead = a.choice.label;
    const summary = draftSummary(play);
    let prompt = draftPrompt(step);
    let ready = isComplete(a.draft);
    if (step?.type === 'pick' && play.stepUi === 'objects' && ((step.max ?? 1) > 1 || step.ordered)) {
      const min = step.min ?? 1, max = step.max ?? 1;
      prompt = `${step.label}: ${a.multi.length} of ${min === max ? min : `${min}–${max}`} selected${step.ordered ? ', in order' : ''}`;
      ready = a.multi.length >= min && a.multi.length <= max;
    } else if (step?.type === 'allocate' && allocation) {
      const problem = validateAllocation(step, allocation);
      prompt = problem ?? step.label;
      ready = !problem;
    } else if (step?.type === 'pick' && play.stepUi === 'objects') {
      prompt = `${step.label}: choose on the table${(step.min ?? 1) === 0 ? ', or confirm to choose none' : ''}`;
      ready = (step.min ?? 1) === 0;
    }
    const rule = tallyRuleFor(tallyRules, a.choice.group);
    let readout: React.ReactNode = null;
    if (rule) {
      const picking = step?.type === 'pick' && play.stepUi === 'objects';
      if (picking && ((step.max ?? 1) > 1 || step.ordered)) prompt = step.label.replace(/ \(\d+ needed\)$/, '');
      readout = <TallyReadout tally={tallyOf(rule, table, picking ? selectedObjectIds(step, a.multi) : [])} selecting={picking} />;
    }
    detail = <>{summary && <span className="bar-summary">{summary}</span>}<span className="bar-prompt">{prompt}</span>{readout}</>;
    actions = (
      <>
        <button type="button" className="bar-button bar-quiet" onClick={play.cancel}>Cancel</button>
        {(a.draft.answers.some(x => !x.forced) || a.multi.length > 0) && <button type="button" className="bar-button bar-quiet" onClick={play.goBack}>Back</button>}
        <button type="button" className="bar-button bar-primary" disabled={!ready || !play.isMyTurn || state.queryCheck?.status === 'pending'} onClick={() => play.confirm(step?.type === 'allocate' && allocation ? allocation : undefined)}>{state.queryCheck?.status === 'pending' ? 'Checking…' : 'Confirm'}</button>
      </>
    );
    if (a.choice.confirm && isComplete(a.draft)) detail = <>{detail}<span className="bar-warning" role="note">{a.choice.confirm}</span></>;
  } else {
    const groups = [...new Set(choices.filter(c => choiceEligible(table, c)).map(c => c.group))];
    // Choice groups the package names as answers to another player's move read "Your response" (a reaction window is not your turn).
    const responding = ((presentationApi.presentation && 'interactions' in presentationApi.presentation ? (presentationApi.presentation as PresentationV2).interactions.responseGroups : undefined) ?? []).some(group => groups.includes(group));
    lead = ownsDecision(table) ? (responding ? 'Your response' : 'Your turn') : 'Available moves';
    detail = groups.length ? (header ? shortList(groups) : groups.join(' · ')) : 'No moves are available.';
    const idleRule = tallyRules ? choices.filter(c => choiceEligible(table, c)).map(c => tallyRuleFor(tallyRules, c.group)).find(Boolean) ?? null : null;
    if (idleRule) detail = <>{detail}<TallyReadout tally={tallyOf(idleRule, table, [])} selecting={false} /></>;
    if (play.auto?.waiting) detail = <>{detail}<span className="bar-auto" role="status">Sending automatically. Turn off auto-pass (game settings or this switch) to answer yourself.</span></>;
    // Rule-routed moves marked `bar` (no object to click, such as ending a season) are also buttons here.
    const bar = [...play.targets.anchors].flatMap(([anchor, entries]) => entries.filter(e => e.bar).map(entry => ({ anchor, entry })));
    idleWithoutPriority = bar.length === 0;
    const drawMove = ({ anchor, entry }: typeof bar[number]) => (
      <button key={entry.choiceId} data-choice={entry.choiceId} type="button" className={`bar-button bar-move${entry.attention ? ' is-attention' : ''}`} onClick={() => play.chooseEntry(anchor, entry)} title={entry.label}>{entry.button ?? entry.label}</button>
    );
    // Small decision sets (for example six sealed declarations) stay directly
    // available to pointer and keyboard users. Large staged menus remain searchable.
    if (bar.length <= 8) actions = bar.map(drawMove);
    else {
      const matches = bar.slice(3).filter(({ entry }) => `${entry.group} ${entry.label}`.toLowerCase().includes(moveQuery.toLowerCase()));
      const pages = Math.max(1, Math.ceil(matches.length / 30)), shownPage = Math.min(movePage, pages - 1);
      actions = <>{bar.slice(0, 3).map(drawMove)}<button className="bar-button" type="button" aria-expanded={moreOpen} onClick={() => {
        const r = barRef.current?.getBoundingClientRect(), root = barRef.current?.closest<HTMLElement>('.scene-root');
        setMoreUp(r && root && r.bottom > window.innerHeight / 2 ? { root, bottom: root.getBoundingClientRect().bottom - r.top + 8 } : null);
        setMoreOpen(v => !v);
      }}>More actions ({bar.length - 3})</button>
        {moreOpen && (menu => moreUp ? createPortal(menu, moreUp.root) : menu)(<div className={`bar-more${moreUp ? ' opens-up' : ''}`} style={moreUp ? { bottom: moreUp.bottom } : undefined} role="dialog" aria-label="More table actions">
          <input className="input" aria-label="Search table actions" placeholder="Search actions…" value={moveQuery} onChange={e => { setMoveQuery(e.target.value); setMovePage(0); }} />
          <div className="bar-more-list">{matches.slice(shownPage * 30, (shownPage + 1) * 30).map(drawMove)}</div>
          {pages > 1 && <nav aria-label="Table action pages"><button className="button" type="button" disabled={!shownPage} onClick={() => setMovePage(shownPage - 1)}>Previous</button><span>{shownPage + 1} / {pages}</span><button className="button" type="button" disabled={shownPage + 1 === pages} onClick={() => setMovePage(shownPage + 1)}>Next</button></nav>}
          {!matches.length && <p>No matching actions.</p>}<button className="button" type="button" onClick={() => setMoreOpen(false)}>Close actions</button>
        </div>)}</>;
    }
    if (treasureChoice && session && !replaying) actions = <>
      <button type="button" className="bar-button bar-primary" disabled={!!state.pending && !playingTreasures}
        onClick={() => setPlayingTreasures(value => !value)}>{playingTreasures ? 'Stop playing Treasures' : 'Play all Treasures'}</button>{actions}
    </>;
  }
  return (
    <section ref={barRef} className={`action-bar${header ? ' is-header' : ''}${idleWithoutPriority ? ' is-idle-without-priority' : ''}${play.isMyTurn ? ' is-my-turn' : ''}${play.active ? ' is-drafting' : ''}${replaying ? ' is-replaying' : ''}${folded && !header ? ' is-folded' : ''}`} aria-label="Action bar" aria-live="polite"
      style={barPlace ? ({ '--bar-bottom': `${barPlace.bottom}px`, '--bar-left': `${barPlace.left}px` } as React.CSSProperties) : undefined}>
      {!header && <button type="button" className="bar-fold" aria-expanded={!folded} aria-label={folded ? 'Show the prompt buttons' : 'Hide the prompt buttons'} title={folded ? 'Show the buttons' : 'Hide the buttons'} onClick={() => setFolded(v => !v)}>{folded ? '▴' : '▾'}</button>}
      <div className="bar-text">
        <p className="bar-lead" title={typeof lead === 'string' ? lead : undefined}>{lead}</p>
        {detail && <p className="bar-detail">{detail}</p>}
        {play.active && <SelectionReview key={play.active.choice.id} rows={play.active.choice.review} />}
        {notice && <p className="bar-error" role="alert">{notice}</p>}
      </div>
      <div ref={actionsRef} className={`bar-actions${actionsScroll ? ' can-scroll' : ''}`}>{actions}{drawerButton}{showUndo && session && (header || state.undo) && <UndoButton session={session} state={state} off={replaying} />}
        {play.auto && (
          <label className="bar-toggle" title={AUTO_ANSWER_TRADEOFF}>
            <input type="checkbox" checked={play.auto.enabled} onChange={e => play.auto?.setEnabled(e.target.checked)} />
            <span>{play.auto.label}</span>
          </label>
        )}
      </div>
    </section>
  );
}

/** "Place a worker · Play a card · Prepare for season" shortened for the header: lower-case after the first. */
function shortList(groups: string[]): string {
  return groups.map((g, i) => (i === 0 ? g : g.charAt(0).toLowerCase() + g.slice(1))).join(', ');
}

// ---- Moves drawer ----------------------------------------------------------------------------

export function MovesDrawer({ play, session, state, table, choices }: { play: PlayApi; session: Session; state: SessionState; table: TableModel; choices: ChoiceView[] }) {
  const [draftChoiceId, setDraftChoiceId] = useState<string | null>(null);
  useEffect(() => { setDraftChoiceId(id => id && choices.find(c => c.id === id && c.scope === 'auxiliary' && choiceEligible(table, c)) ? id : null); }, [state.revision, state.decisionId]);
  useEffect(() => { setDraftChoiceId(null); }, [session, state.gameId, state.matchId, state.seat]);
  if (!play.drawerOpen) return null;
  return (
    <div className="drawer-scrim" onClick={() => play.setDrawerOpen(false)}>
      <aside className="moves-drawer" role="dialog" aria-label="All moves" onClick={e => e.stopPropagation()}>
        <div className="drawer-head">
          <h2>All moves</h2>
          <button type="button" className="icon-button" aria-label="Close all moves" onClick={() => play.setDrawerOpen(false)}>×</button>
        </div>
        {play.targets.unresolved.length > 0 && <p className="drawer-note">{play.targets.unresolved.length} move(s) have no table target and are only here.</p>}
        <ChoicePanel session={session} state={state} table={table} choices={choices} isMyTurn={play.isMyTurn}
          draftChoiceId={draftChoiceId} onDraft={setDraftChoiceId} onInspect={id => play.inspect(id)} onPickTargets={() => undefined} />
      </aside>
    </div>
  );
}

// ---- Turn banner ---------------------------------------------------------------------------

export function TurnBanner({ text, color }: { text: string | null; color: string | null }) {
  if (!text) return null;
  return <div className="turn-banner" role="status" style={color ? ({ '--banner-color': color } as React.CSSProperties) : undefined}>{text}</div>;
}

// ---- All overlays ----------------------------------------------------------------------------

/**
 * On a phone the page scrolls under a docked prompt. When a step lights targets and none of them is visible above the prompt
 * (picking hand cards while the table is scrolled to the villain), bring the first one into view once per step. A wide scene
 * does the same for a scrolling pane: when no lit target can be seen (the board area scrolled away from the cards a search
 * shows, with the action list in another pane), the first one is scrolled into view, by the least movement that shows it.
 */
function useRevealLitTargets(play: PlayApi, decision: string | null) {
  const litKey = play.lit.join('|');
  // A width change (rotating the phone, crossing into the narrow scene) is a new layout; a height change (the address bar) is not.
  const [layoutWidth, setLayoutWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => { clearTimeout(timer); timer = setTimeout(() => setLayoutWidth(w => (w === window.innerWidth ? w : window.innerWidth)), 250); };
    window.addEventListener('resize', onResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize); };
  }, []);
  // A wide scene reveals once per decision, not once per observation: the lit targets of the last handled observation, and whether the
  // player has scrolled since (a scroll made during a decision is theirs; the next observation of that decision never undoes it).
  const handledLit = useRef<ReadonlySet<string>>(new Set());
  // `decision` is the identity of the pending decision (`decisionIdentity`), not the server's decision id, which is one per revision.
  const handledDecision = useRef<string | null | undefined>(undefined);
  const lastInput = useRef(0);
  const scrolledSinceReveal = useRef(false);
  const lastWidth = useRef(layoutWidth);
  useEffect(() => {
    const mark = () => { lastInput.current = performance.now(); scrolledSinceReveal.current = true; };
    // Space or Enter that activates a focused button is not a scroll; neither are arrow keys that move a caret or an option.
    const key = (event: KeyboardEvent) => {
      const t = event.target instanceof Element ? event.target : null;
      if (keyScrolls(event.key, t && { tagName: t.tagName, isContentEditable: (t as HTMLElement).isContentEditable, role: t.getAttribute('role') })) mark();
    };
    window.addEventListener('wheel', mark, { capture: true, passive: true });
    window.addEventListener('touchmove', mark, { capture: true, passive: true });
    window.addEventListener('keydown', key, true);
    return () => { window.removeEventListener('wheel', mark, true); window.removeEventListener('touchmove', mark, true); window.removeEventListener('keydown', key, true); };
  }, []);
  useEffect(() => {
    const root = document.querySelector('.scene-root');
    if (!root) return undefined;
    const narrow = root.classList.contains('is-narrow');
    if (!play.isMyTurn || play.lit.length === 0) {
      if (!narrow) { handledLit.current = new Set(); handledDecision.current = undefined; scrolledSinceReveal.current = false; }
      return undefined;
    }
    const scheduled = performance.now();
    const frame = requestAnimationFrame(() => {
      const bar = narrow ? root.querySelector('.action-bar:not(.is-header)')?.getBoundingClientRect() : undefined;
      const floor = bar && bar.height > 0 ? bar.top - 8 : window.innerHeight;
      const elements = play.lit.map(a => root.querySelector<HTMLElement>(`[data-anchor="${CSS.escape(a)}"]`)).filter((el): el is HTMLElement => !!el && el.getClientRects().length > 0);
      if (elements.length === 0) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      elements.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      if (narrow) {
        if (elements.some(el => { const r = el.getBoundingClientRect(); return r.top >= 0 && r.bottom <= floor; })) return;
        revealElement(elements[0]!, { block: 'center', behavior, floor });
        return;
      }
      // Wide: a decision that starts (or a new layout width) reveals; the same decision observed again does not.
      // A decision that follows another reveals whatever the player scrolled before; targets that join the decision under way keep
      // the player's own scroll position.
      const previous = handledLit.current, relaid = lastWidth.current !== layoutWidth;
      const reveal = shouldReveal({ previousLit: previous, lit: play.lit, previousDecision: handledDecision.current, decision, relaid, scrolledSinceReveal: scrolledSinceReveal.current });
      handledLit.current = new Set(play.lit); handledDecision.current = decision; lastWidth.current = layoutWidth;
      if (!reveal) return;
      scrolledSinceReveal.current = false;
      if (lastInput.current > scheduled) return; // the player scrolled while this reveal was waiting
      if (elements.some(el => mostlyVisible(el, floor))) return;
      // Bring the region the card belongs to into view with its heading when that fits where it is shown, else the card itself
      // (from its top when it is taller than its pane).
      const first = elements[0]!, zone = first.closest<HTMLElement>('.brick-zone');
      const target = zone && zone.getBoundingClientRect().height <= roomHeight(zone, floor) ? zone : first;
      const tall = target.getBoundingClientRect().height > roomHeight(target, floor);
      revealElement(target, { block: tall ? 'start' : 'nearest', behavior, floor });
    });
    return () => cancelAnimationFrame(frame);
    // One reveal per step; lit anchors of the same step do not repeat it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play.stepKey, litKey, layoutWidth, decision]);
}

export function PlayOverlays({ session, state, table, choices, promptHost = null, replaying = false, compactShell = false, inlineInspector = false }: { session: Session; state: SessionState; table: TableModel; choices: ChoiceView[]; promptHost?: HTMLElement | null; replaying?: boolean; compactShell?: boolean; inlineInspector?: boolean }) {
  const play = usePlay()!;
  // The same pending decision observed across revisions keeps one identity, so a peer locking a simultaneous decision or a draft kept
  // across a revision never reveals again; a different set of choices is a new decision.
  const decision = useMemo(() => decisionIdentity(play.active?.choice, choices.filter(c => choiceEligible(table, c))), [play.active?.choice, choices, table]);
  useRevealLitTargets(play, decision);
  const step = play.active ? currentStep(play.active.draft) : null;
  const allocStep = step?.type === 'allocate' ? step : null;
  // Values belong to this exact step object, so a later draft never inherits an earlier tray.
  const [allocation, setAllocation] = useState<{ step: ChoiceStep; values: number[] } | null>(null);
  const values = useMemo(() => (allocStep ? (allocation?.step === allocStep ? allocation.values : initialAllocation(allocStep)) : null), [allocStep, allocation]);
  const showStep = !!play.active && play.stepUi === 'popover';
  return (
    <>
      {play.chooser && <ChoicePopover play={play} choices={choices} table={table} />}
      {showStep && <StepPopover play={play} allocation={values} setAllocation={v => allocStep && setAllocation({ step: allocStep, values: v })} />}
      {!inlineInspector && <CardZoom play={play} table={table} />}
      <AltHoverCard table={table} />
      {promptHost
        ? createPortal(<ActionBar play={play} table={table} state={state} choices={choices} allocation={values as StepValue as number[] | null} session={session} header replaying={replaying} showUndo={!compactShell} />, promptHost)
        : <ActionBar play={play} table={table} state={state} choices={choices} allocation={values as StepValue as number[] | null} session={session} replaying={replaying} />}
      <MovesDrawer play={play} session={session} state={state} table={table} choices={choices} />
    </>
  );
}
